import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Loader2, Users, Link, LogOut, UserPlus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import InviteFriendsModal from "@/components/InviteFriendsModal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  subscribeToSession,
  subscribeToMembers,
  advanceStatus,
  submitPreferences,
  generateGroupQueue,
  leaveGroupSession,
} from "@/lib/groupSession";
import GoPremiumModal from "@/components/GoPremiumModal";
import type { GroupSessionData } from "@/components/GroupLobby";
import type { MovieData } from "@/components/SwipeCard";

const movieGenres = [
  "Action", "Adventure", "Animation", "Comedy", "Crime", "Documentary",
  "Drama", "Family", "Fantasy", "History", "Horror", "Music",
  "Mystery", "Romance", "Sci-Fi", "Thriller", "War", "Western"
];
const tvGenres = [
  "Action & Adventure", "Animation", "Comedy", "Crime", "Documentary",
  "Drama", "Family", "History", "Horror", "Kids", "Music", "Mystery",
  "Reality", "Romance", "Sci-Fi & Fantasy", "Soap", "Talk", "Thriller", "War & Politics", "Western"
];
const decades = ["any", "Last 12 months", "2020s", "2010s", "2000s", "1990s", "1980s", "1970s", "1960s & earlier"];

interface GroupWaitingRoomProps {
  groupData: GroupSessionData;
  onQueueReady: (queue: MovieData[], session: any) => void;
  onLeave: () => void;
}

const GroupWaitingRoom = ({ groupData, onQueueReady, onLeave }: GroupWaitingRoomProps) => {
  const { t } = useTranslation();
  const [session, setSession] = useState(groupData.session);
  const [members, setMembers] = useState<any[]>([]);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>(["any"]);
  const [selectedDecades, setSelectedDecades] = useState<string[]>(["any"]);
  const [prefsSubmitted, setPrefsSubmitted] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [includeBookmarks, setIncludeBookmarks] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [rejectedJoinName, setRejectedJoinName] = useState<string | null>(null);
  const { user } = useAuth();
  const hasLeft = useRef(false);
  const lastRejectedTs = useRef<string | null>(null);

  const isHost = groupData.isHost;
  const status = session.status;
  const guestId = groupData.member.guest_id || undefined;

  useEffect(() => {
    const sessionChannel = subscribeToSession(session.id, (updated) => {
      setSession(updated);
      if (updated.status === 'closed') {
        onLeave();
      }
      if (isHost && updated.last_rejected_join) {
        const rj = updated.last_rejected_join as { display_name: string; timestamp: string };
        if (rj.timestamp !== lastRejectedTs.current) {
          lastRejectedTs.current = rj.timestamp;
          setRejectedJoinName(rj.display_name);
        }
      }
    }, guestId);
    const membersChannel = subscribeToMembers(session.id, setMembers, guestId);
    return () => { sessionChannel.unsubscribe(); membersChannel.unsubscribe(); };
  }, [session.id]);

  const transitionedToSwiping = useRef(false);

  useEffect(() => {
    const memberId = groupData.member.id;
    const sessionId = session.id;

    const fireLeave = () => {
      if (hasLeft.current || transitionedToSwiping.current) return;
      hasLeft.current = true;
      leaveGroupSession(memberId, sessionId).catch(() => {});
    };

    window.addEventListener('beforeunload', fireLeave);
    return () => {
      window.removeEventListener('beforeunload', fireLeave);
      fireLeave();
    };
  }, [groupData.member.id, session.id]);

  const handleLeave = async () => {
    if (hasLeft.current) return;
    hasLeft.current = true;
    setLeaving(true);
    try {
      await leaveGroupSession(groupData.member.id, session.id);
    } catch {}
    onLeave();
  };

  useEffect(() => {
    if (status === 'swiping' && session.queue) {
      transitionedToSwiping.current = true;
      onQueueReady(session.queue as MovieData[], session);
    }
  }, [status, session.queue]);

  const shareLink = `${window.location.origin}/discover?join=${session.room_code}`;

  const copyCode = () => {
    navigator.clipboard.writeText(session.room_code);
    setCopied(true);
    toast.success(t("groupWaiting.codeCopied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    toast.success(t("groupWaiting.linkCopied"));
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const toggleGenre = (g: string) => {
    if (g === "any") { setSelectedGenres(["any"]); return; }
    setSelectedGenres(prev => {
      const without = prev.filter(x => x !== "any");
      const next = without.includes(g) ? without.filter(x => x !== g) : [...without, g];
      return next.length === 0 ? ["any"] : next;
    });
  };

  const toggleDecade = (d: string) => {
    if (d === "any") { setSelectedDecades(["any"]); return; }
    setSelectedDecades(prev => {
      const without = prev.filter(x => x !== "any");
      const next = without.includes(d) ? without.filter(x => x !== d) : [...without, d];
      return next.length === 0 ? ["any"] : next;
    });
  };

  const handleSubmitPrefs = async () => {
    try {
      await submitPreferences(groupData.member.id, selectedGenres, selectedDecades);
      setPrefsSubmitted(true);
      toast.success(t("groupWaiting.prefsSubmitted"));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleStartPrefs = async () => {
    try {
      await advanceStatus(session.id, 'preferences');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleGenerateQueue = async () => {
    setGenerating(true);
    try {
      let providerIds: number[] = [];
      let region = 'GB';
      try {
        const raw = localStorage.getItem("ayeris_providers");
        providerIds = raw ? JSON.parse(raw) : [];
        region = localStorage.getItem("ayeris_region") || 'GB';
      } catch {}
      await generateGroupQueue(session.id, providerIds, region);
    } catch (e: any) {
      toast.error(e.message);
      setGenerating(false);
    }
  };

  const allPrefsReady = members.length > 0 && members.every(m => m.preferences_ready);
  const genreList = session.media_type === 'movie' ? movieGenres : tvGenres;

  return (
    <div className="h-[100dvh] bg-background flex items-center justify-center px-4 py-4 overflow-y-auto">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md bg-gradient-card border border-border rounded-2xl p-6 shadow-card space-y-5"
      >
        {/* Room Code Header */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground tracking-wider mb-1">{t("groupWaiting.roomCode")}</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-3xl font-display font-bold text-primary tracking-[0.15em]">{session.room_code}</span>
            <button onClick={copyCode} className="p-2 rounded-lg hover:bg-secondary transition-colors">
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {session.media_type === 'movie' ? `🎬 ${t("common.movie")}` : `📺 ${t("common.tvShow")}`} • {session.decision_timer}min {t("group.decisionTimer").toLowerCase()}
          </p>

          <div className="mt-3 flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-2 mx-auto max-w-xs">
            <Link className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-[11px] text-muted-foreground truncate flex-1 text-left">{shareLink}</span>
            <button onClick={copyLink} className="p-1.5 rounded-md hover:bg-secondary transition-colors shrink-0">
              {linkCopied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-muted-foreground" />}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">{t("groupWaiting.shareLink")}</p>
        </div>

        {/* Members List */}
        <div>
          <p className="text-xs text-muted-foreground tracking-wider mb-2 flex items-center gap-1">
            <Users className="w-3 h-3" /> {t("groupWaiting.members")} ({members.length})
          </p>
          <div className="space-y-1.5">
            {members.map(m => (
              <div key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-secondary/50">
                <span className="text-sm text-foreground">
                  {m.display_name}
                  {m.id === session.host_member_id && <span className="ml-1.5 text-[10px] text-primary font-medium">{t("groupWaiting.host")}</span>}
                </span>
                {status === 'preferences' && (
                  <span className={`text-[10px] font-medium ${m.preferences_ready ? 'text-primary' : 'text-muted-foreground'}`}>
                    {m.preferences_ready ? t("groupWaiting.ready") : t("groupWaiting.picking")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Lobby state */}
        {status === 'lobby' && (
          <div className="text-center space-y-3">
            {user && (
              <button
                onClick={() => setShowInviteFriends(true)}
                className="w-full py-2.5 rounded-xl bg-secondary text-foreground text-sm font-medium border border-border active:scale-95 transition-transform flex items-center justify-center gap-2"
              >
                <UserPlus className="w-4 h-4 text-primary" /> {t("groupWaiting.inviteFriends")}
              </button>
            )}
            {isHost ? (
              <button
                onClick={handleStartPrefs}
                disabled={members.length < 2}
                className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform disabled:opacity-50"
              >
                {members.length < 2 ? t("groupWaiting.waitingForOthers") : t("groupWaiting.everyonesIn")}
              </button>
            ) : (
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{t("groupWaiting.waitingForHost")}</span>
              </div>
            )}
          </div>
        )}

        {/* Preferences state */}
        {status === 'preferences' && !prefsSubmitted && (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground tracking-wider mb-2 block">{t("groupWaiting.yourGenres")}</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => toggleGenre("any")}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all active:scale-95 ${
                    selectedGenres.includes("any") ? "bg-primary text-primary-foreground shadow-iris" : "bg-secondary text-secondary-foreground"
                  }`}
                >{t("common.any")}</button>
                {genreList.map(g => (
                  <button
                    key={g}
                    onClick={() => toggleGenre(g)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all active:scale-95 ${
                      selectedGenres.includes(g) ? "bg-primary text-primary-foreground shadow-iris" : "bg-secondary text-secondary-foreground"
                    }`}
                  >{t("genreLabels." + g, g)}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground tracking-wider mb-2 block">{t("groupWaiting.yourEra")}</label>
              <div className="flex flex-wrap gap-1.5">
                {decades.map(d => (
                  <button
                    key={d}
                    onClick={() => toggleDecade(d)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all active:scale-95 ${
                      selectedDecades.includes(d) ? "bg-primary text-primary-foreground shadow-iris" : "bg-secondary text-secondary-foreground"
                    }`}
                  >{d === "any" ? t("common.any") : t("decadeLabels." + (d === "Last 12 months" ? "last12months" : d === "1960s & earlier" ? "1960sEarlier" : d), d)}</button>
                ))}
              </div>
            </div>

            <button
              onClick={handleSubmitPrefs}
              className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform"
            >
              {t("groupWaiting.submitPrefs")}
            </button>
          </div>
        )}

        {/* Preferences submitted */}
        {status === 'preferences' && prefsSubmitted && (
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2 text-primary">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">{t("groupWaiting.prefsIn")}</span>
            </div>
            {isHost && allPrefsReady && members.length >= 2 && (
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer justify-center">
                  <input
                    type="checkbox"
                    checked={includeBookmarks}
                    onChange={(e) => setIncludeBookmarks(e.target.checked)}
                    className="w-4 h-4 rounded border-border accent-primary"
                  />
                  <span className="text-sm text-muted-foreground">{t("groupWaiting.includeBookmarks")}</span>
                </label>
                <button
                  onClick={handleGenerateQueue}
                  disabled={generating}
                  className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("groupWaiting.generatingText")}</> : t("groupWaiting.generateQueue")}
                </button>
              </div>
            )}
            {isHost && members.length < 2 && (
              <p className="text-sm text-muted-foreground">{t("groupWaiting.needMoreMembers")}</p>
            )}
            {(!isHost || !allPrefsReady) && (
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{allPrefsReady ? t("groupWaiting.waitingForHostText") : t("groupWaiting.waitingForEveryone")}</span>
              </div>
            )}
          </div>
        )}

        {/* Generating queue */}
        {status === 'generating' && (
          <div className="text-center py-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{t("groupWaiting.generatingQueue")}</p>
          </div>
        )}

        {/* Rejected join notification */}
        <AnimatePresence>
          {rejectedJoinName && isHost && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-2"
            >
              <p className="text-xs text-foreground">
                {t("groupWaiting.rejectedJoin", { name: rejectedJoinName })}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPremiumModal(true)}
                  className="flex-1 py-2 rounded-lg bg-gradient-iris text-primary-foreground text-xs font-semibold active:scale-95 transition-transform"
                >
                  {t("groupWaiting.goPremiumUnlock")}
                </button>
                <button
                  onClick={() => setRejectedJoinName(null)}
                  className="px-3 py-2 rounded-lg bg-secondary text-muted-foreground text-xs font-medium active:scale-95 transition-transform"
                >
                  {t("groupWaiting.dismiss")}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Leave button */}
        <button
          onClick={handleLeave}
          disabled={leaving}
          className="w-full py-2.5 rounded-xl bg-secondary text-muted-foreground text-sm font-medium border border-border active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {leaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
          {t("groupWaiting.leaveSession")}
        </button>
      </motion.div>

      {/* Invite Friends Modal */}
      <InviteFriendsModal
        open={showInviteFriends}
        onClose={() => setShowInviteFriends(false)}
        sessionId={session.id}
      />

      {/* Premium Upsell Modal */}
      <GoPremiumModal 
        open={showPremiumModal} 
        onClose={() => setShowPremiumModal(false)} 
        trigger="group_limit" 
      />
    </div>
  );
};

export default GroupWaitingRoom;
